import "./SlideList.scss";

import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";

import { Logo } from "../../../Assets/Logos";
import { generateConfidentPhrase } from "../../Utilities/ConfidentPhraseGenerator";
import Text from "../Text/Text";

interface SlideListProps {
  images: Logo[];
  activeIdx?: number;
  deltaY?: number;
  ySteps?: number;
  transition?: {
    duration: number;
    ease: string;
  };
  delay?: number;
  showTaglines?: boolean;
  enlarge?: boolean;
  reverse?: boolean;
}

const OFFSET = 1;

const getVisCount = () => {
  const result = window.innerWidth / getImgWidth();
  return result;
};
const isExactCenter = (idx: number) => center() === idx;
const center = () => Math.floor(getVisCount() / 2);
const getCenterSize = () => {
  return window.innerWidth >= 1500 ? 2 : 1;
};
const getCenterOffsetIdx = (idx: number) =>
  Math.max(0, Math.abs(center() - idx) - getCenterSize());
// FIFO order with index 1 "in front"
const sortImages = (images: Logo[], curIdx: number) =>
  images
    .slice((curIdx - 1) % images.length)
    .concat(images.slice(0, (curIdx - 1) % images.length));
// Best estimate pixel width before render (match SASS)
const getImgWidth = () => {
  //  EDIT IF CSS SIZING IS CHANGED
  return window.innerWidth >= 1500
    ? 192
    : window.innerWidth > 750
    ? 144
    : window.innerWidth > 500
    ? 126
    : 90;
};
const getY = (
  ySteps: number,
  index: number,
  deltaY: number,
  reverse: boolean
) => [
  `${
    Math.min(ySteps, Math.max(getCenterOffsetIdx(index - OFFSET), 0)) *
    (reverse ? -deltaY : deltaY)
  }%`,
  `${
    Math.min(
      ySteps,
      Math.max(getCenterOffsetIdx(index - OFFSET + (reverse ? -10 : 1)), 0)
    ) * deltaY
  }%`
];

export const SlideList: React.FC<SlideListProps> = ({
  images,
  deltaY = 0,
  ySteps = 0,
  delay = 5000,
  transition = {
    duration: 1.5,
    ease: "anticipate"
  },
  showTaglines = false,
  enlarge = false,
  reverse = false
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(currentImageIndex + 1);
    }, delay);
    return () => clearInterval(interval);
  }, [currentImageIndex, images.length, delay]);

  const curIdx = (currentImageIndex + 1) % images.length;
  const orderedImages = reverse
    ? sortImages(images, curIdx).reverse()
    : sortImages(images, curIdx);
  return (
    <>
      <div className="fslide-container">
        <motion.div
          key={currentImageIndex - 1} // same key value for the entire journey to front of queue
          className={`fslide-image fslide-firstSpacer`}
          initial={{ width: reverse ? 0 : getImgWidth() }}
          animate={{ width: reverse ? getImgWidth() : 0 }}
          transition={transition}
        />
        {orderedImages.map((image, index) => {
          const [y, prevY] = getY(ySteps, index, deltaY, reverse);
          return (
            <motion.div
              key={index + currentImageIndex} // same key value for the entire journey to front of queue
              className={`fslide-image fslide-mainImage ${
                isExactCenter(index - OFFSET) ? "fslide-center" : ""
              }`}
              initial={{
                x: "-150%",
                y: prevY
              }}
              animate={{ y }}
              transition={transition}
            >
              <motion.img
                src={image.src}
                alt={image.name}
                initial={{ scale: 1 }}
                animate={{
                  scale: enlarge && isExactCenter(index - OFFSET) ? 3 : 1
                }}
                className="fslide-imageContent"
              />
            </motion.div>
          );
        })}
      </div>
      {showTaglines && (
        <motion.div
          initial={{ opacity: 0 }}
          transition={{
            duration: delay / 1000,
            repeatType: "loop",
            repeat: Infinity,
            ease: "linear",
            type: "tween"
          }}
          animate={{
            opacity: [
              0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1,
              0
            ]
          }}
        >
          <Text key={currentImageIndex} h3 center className="fslide-tagline">
            {generateConfidentPhrase(
              sortImages(images, curIdx)[center() + OFFSET].name
            )}
          </Text>
        </motion.div>
      )}
    </>
  );
};

export default SlideList;

// export const SlideRight: React.FC<SlideListProps> = ({
//   images,
//   offset = undefined
// }) => {
//   const [currentImageIndex, setCurrentImageIndex] = useState(0);
//   useEffect(() => {
//     const interval = setInterval(
//       () => {
//         setCurrentImageIndex(currentImageIndex + 1);
//       },
//       1000
//       // Math.random() > 0.5 ? 3500 : Math.random() > 0.5 ? 3000 : 2500
//     );
//     return () => clearInterval(interval);
//   }, [currentImageIndex, images.length]);

//   const curIdx = (currentImageIndex + 1) % images.length;
//   // switch order for left to right
//   const sortedImages = images
//     .slice(curIdx)
//     .concat(images.slice(0, curIdx))
//     .reverse();
//   return (
//     <div className={`slide-container  ${offset}`}>
//       {sortedImages.map((image, index) => {
//         const X = `translateX(${index * 100 - 100}%)`;
//         return (
//           <div
//             key={index - currentImageIndex}
//             className={`slide-image`}
//             style={{
//               transform: `${X}`
//             }}
//           >
//             <img src={image} alt={image} className="slide-imageContent" />
//           </div>
//         );
//       })}
//     </div>
//   );
// };
